'use client'

import { Dialog, type DialogAPI } from '@fukku/dialog/Dialog'
import { useClosingAnimation } from '@fukku/dialog/hooks/useClosingAnimation'
import type { DialogProps } from '@fukku/dialog/types/props'
import { cx } from 'classix'
import { forwardRef, useImperativeHandle, useRef } from 'react'

import { DialogContextProvider, DialogType } from '../hooks/useDialogContext'
import { apiFactory } from '../utils/apiFactory'
import styles from './SideSheet.module.css'

export type SideSheetProps = DialogProps & {
	/** Appears from left side */
	readonly left?: boolean
	/** A side sheet modal with a most large width content */
	readonly extraWidth?: boolean
}

export function SideSheetComponent(
	{
		children,
		title,
		onOpen,
		onClose,
		className,
		left = false,
		extraWidth = false,
		preventClose = false,
		keepContent = false,
		'data-testid': dataTestId,
	}: SideSheetProps,
	ref: React.Ref<DialogAPI>
) {
	const dialogRef = useRef<HTMLDialogElement>(null)

	const { opened, closing, setOpened } = useClosingAnimation(dialogRef)

	const api = apiFactory(setOpened, dialogRef.current)

	useImperativeHandle(ref, () => api, [api])

	return (
		<DialogContextProvider
			type={DialogType.SideSheet}
			api={api}
			extraWidth={extraWidth}
			preventClose={preventClose}
		>
			<Dialog
				title={title}
				className={cx(
					styles.side,
					left && styles.fromLeft,
					closing && styles.closing,
					className
				)}
				ref={dialogRef}
				preventClose={preventClose}
				keepContent={keepContent}
				setOpened={setOpened}
				opened={opened}
				onOpen={onOpen}
				onClose={onClose}
				data-testid={dataTestId}
			>
				<div className={cx(styles.inner, extraWidth && styles.largeContent)}>
					{children}
				</div>
			</Dialog>
		</DialogContextProvider>
	)
}

export const SideSheet = forwardRef(SideSheetComponent)
